import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

export interface IUpdateEnableNotificationsParams {
	credentialId: number;
	enableNotifications: boolean;
}

interface IUpdateEnableNotificationsState {
	loading: boolean;
	error: string | null;
}

const initialState: IUpdateEnableNotificationsState = {
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
	};
};

const buildUrl = (credentialId: number, enableNotifications: boolean) => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	const searchParams = new URLSearchParams();
	searchParams.set("credentialId", String(credentialId));
	searchParams.set("enableNotifications", String(enableNotifications));
	return `${baseUrl.replace(/\/$/, "")}/Credentials/UpdateEnableNotifications?${searchParams.toString()}`;
};

export const updateEnableNotifications = createAsyncThunk<boolean, IUpdateEnableNotificationsParams>(
	"passwordManager/updateEnableNotifications",
	async ({ credentialId, enableNotifications }, { rejectWithValue, dispatch }) => {
		try {
			const response = await withPasswordManagerRefresh(dispatch, () =>
				axios.put<boolean>(buildUrl(credentialId, enableNotifications), undefined, { headers: getAuthHeaders() }),
			);
			return response.data;
		} catch (error: unknown) {
			return rejectWithValue(error instanceof Error ? error.message : "Failed to update notifications");
		}
	},
);

const updateEnableNotificationsSlice = createSlice({
	name: "passwordManager/updateEnableNotifications",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(updateEnableNotifications.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateEnableNotifications.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(updateEnableNotifications.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to update notifications";
			});
	},
});

export const updateEnableNotificationsReducer = updateEnableNotificationsSlice.reducer;
export { updateEnableNotificationsSlice };
